import { SITE } from '@/app/constants';
import { calculateDaysUntilUTC } from '@/app/utils';
import { GptImage25Banner } from './components/GptImage25Banner';
import { RetentionBanner } from './components/RetentionBanner';
import { Vidu7DTrialBanner } from './components/Vidu7DTrialBanner';
import type { AnnouncementConfig } from './types';

/**
 * 顶部公告注册表
 *
 * 调度规则：
 * - 同一时间只展示一条公告，取 priority 最高且满足条件的
 * - priority 值越大优先级越高；新活动想覆盖旧活动，给更高 priority 即可
 * - expiresAt（可选）：UTC 过期时间，到期后自动隐藏；省略表示长驻
 * - condition（可选）：运行时条件函数，不满足时跳过，自动 fallback 到下一条
 * - 用户点击关闭后永久隐藏（localStorage），不影响其他公告
 *
 * 新增公告：在 components/ 下创建 Banner 组件，然后在此数组追加一条配置
 */
export const ANNOUNCEMENTS: AnnouncementConfig[] = [
  /**
   * @popupTitle Vidu 7D 试用顶部横幅
   * @popupSite global
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   * @popupCondition 海外站 + 非团队空间 + 未订阅（subs_plan === 'free'）+ 具备首购优惠资格 + 非 t1 地区
   */
  {
    id: 'vidu_7d_trial_banner',
    priority: 400,
    component: (props) => <Vidu7DTrialBanner {...props} />,
    condition: (ctx) =>
      SITE === 'global' &&
      !ctx.isTeamSpace &&
      ctx.userInfo?.subs_plan === 'free' &&
      ctx.isSubOneTimeOffer &&
      ctx.countryLevel != null &&
      ctx.countryLevel !== 't1',
  },
  /**
   * @popupTitle 取消续费挽留横幅 - 最后一天
   * @popupSite china
   * @popupCondition 用户已取消续费 + 未处于提前续费冷却期 + 距到期 1 天内
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   * @popupNote 优先级最高，覆盖临期 7 天横幅
   */
  {
    id: 'retention_last_day',
    priority: 210,
    component: (props) => <RetentionBanner {...props} phase="lastDay" />,
    condition: (ctx) => {
      if (SITE !== 'china') return false;
      const sub = ctx.subscription;
      if (!sub?.auto_renew_canceled || sub.advance_renew_in_cooldown || !sub.end_time) return false;
      const days = calculateDaysUntilUTC(sub.end_time);
      return days > 0 && days <= 1;
    },
  },
  /**
   * @popupTitle 取消续费挽留横幅 - 临期 7 天
   * @popupSite china
   * @popupCondition 用户已取消续费 + 未处于提前续费冷却期 + 距到期 1~7 天
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   */
  {
    id: 'retention_expiring_7d',
    priority: 200,
    component: (props) => <RetentionBanner {...props} phase="expiring" />,
    condition: (ctx) => {
      if (SITE !== 'china') return false;
      const sub = ctx.subscription;
      if (!sub?.auto_renew_canceled || sub.advance_renew_in_cooldown || !sub.end_time) return false;
      const days = calculateDaysUntilUTC(sub.end_time);
      return days > 1 && days <= 7;
    },
  },
  /**
   * @popupTitle Image 2.5 顶部公告
   * @popupSite both
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   * @popupCondition 国内外站点；无更高优先级公告时展示
   * @popupNote 点击进入参考生图并选择 Image 2.5 Fast
   */
  {
    id: 'gpt_image_2_5',
    priority: 100,
    component: GptImage25Banner,
  },
];
